import cn from 'classnames';

/**
 * LF360-1823: Tailwind class strings for the Figma-aligned Behavior Rules modal
 * (Figma node 14328:41230).
 *
 * Every value in the design already exists in the flow-designer theme
 * (src/index.css), so nothing here inlines a colour:
 *   pill bg rgba(108,108,137,0.08) -> --input        -> bg-input
 *   text     #6C6C89               -> --label-foreground -> text-label-foreground
 *   text     #151515               -> --foreground   -> text-foreground
 *   radius   8px                   -> --radius       -> rounded-lg
 *   radius   6px                   -> --radius - 2   -> rounded-md
 *   divider  rgba(108,108,137,0.16)-> --separator    -> border-separator
 *
 * The design specifies Inter 13px for the branch label; the app is Sora
 * globally (src/index.css:93) and Sora is used instead - deliberate deviation.
 */

/**
 * Grey pill styling for a criterion control. Figma: pl-8 pr-7 py-7, 12px text.
 * `border-0`, `shadow-none` and `h-auto` are not redundant - they neutralise
 * the shadcn `Input`'s own base chrome (`h-9 border border-input shadow-sm`
 * in `@/components/ui/input.tsx`), which this className is merged into via
 * `tailwind-merge` and otherwise still renders on every numeric pill.
 * No `flex`/`items-center`/`gap-*` here - every call site applies this
 * directly to a native `<input>`, which has no children to lay out.
 */
export const rulePillClasses = ({ disabled }: { disabled?: boolean } = {}): string =>
  cn(
    'rounded-lg border-0 bg-input pl-2 pr-[7px] py-[7px] h-auto shadow-none text-xs text-foreground whitespace-nowrap',
    { 'opacity-50': disabled },
  );

/** And/Or segmented control. Figma: 2px padded group, active segment at 6px radius. */
export const andOrToggleClasses = () => ({
  group: 'flex w-fit items-center rounded-lg border border-input p-[2px]',
  active: 'flex items-center rounded-md bg-input pl-2 pr-[7px] py-[5px] text-xs text-foreground',
  inactive: 'flex w-10 items-center justify-center pl-2 pr-[7px] py-[5px] text-xs text-foreground opacity-30',
});

/** "Branch N" section label. Figma: 13px secondary text. */
export const branchHeaderClasses = (): string =>
  'flex h-[31px] items-center text-[13px] tracking-[0.1px] text-label-foreground';

/** "+ Add rule" / "+ Add branch" row. Figma: 32px tall, 12px secondary text. */
export const addRowClasses = (): string =>
  'flex h-8 items-center gap-1 text-xs tracking-[0.1px] text-label-foreground';

/**
 * One branch block, separated from the previous by a hairline. Stacks its rules
 * 8px apart - see the LF360-1933 note on criteriaRowClasses below for why this
 * value and the one there are easy to swap by accident.
 */
export const branchBlockClasses = (): string =>
  'flex flex-col gap-2 border-t border-separator px-4 pb-4 pt-2';

/**
 * One criterion: its controls wrap at 4px gaps, with room reserved for the
 * delete button. No bare `group` here - the delete button that used to want
 * a row-scoped hover target uses the named `group/row` added at its own
 * wrapper (BehaviorRulesNodeInfo.tsx) instead, and the drag handle it would
 * otherwise have shadowed is a sibling of every row, not a descendant, so an
 * unnamed `group` on the row never did anything for it.
 *
 * LF360-1933: this gap and branchBlockClasses' were previously each other's -
 * a rule was looser inside than it was from its neighbour, which reads as one
 * evenly-spaced wall of pills with no rule boundary. The trap is that both
 * values are plausible in isolation and the wrong pairing looks deliberate;
 * only the two together say which is which. Figma node 7643:34271 settles it:
 * within a rule, `Frame 455` ends a row at x=175 and starts the next at x=179,
 * and `Frame 456` ends one at y=31 and starts the next at y=35 - 4px both ways;
 * between rules, the rule column stacks its frames at y=0(h31), 39, 78 - 8px.
 * Both halves are pinned in styles.test.ts, deliberately including the negative
 * half, because a one-sided assertion still passes against the swapped pair.
 */
export const criteriaRowClasses = (): string =>
  'relative flex flex-wrap items-center gap-1 pr-8';

/**
 * LF360-1933: the ghost `Button` wrapping a rule's 16px trash glyph.
 *
 * "No height" and "no padding" have to be written as *explicitly zeroed* here,
 * not merely left unsaid. This string is merged into the shadcn `Button`'s own
 * base chrome by `tailwind-merge` (@/components/ui/button.tsx via @/lib/utils),
 * and anything it does not contradict survives the merge. That is precisely
 * what produced the reported defect: the previous inline className set a colour
 * and a width, so it contradicted the button's horizontal padding and nothing
 * else - the button's default height and vertical padding came through intact
 * and the box rendered 36px tall around a glyph that should be 16, pushing the
 * glyph below the centre of the rule's first line. Sizing the box to the glyph
 * and zeroing the padding on both axes is what makes the merge resolve.
 *
 * `controlVariantClasses('pill')` below performs the identical neutralisation
 * against the shadcn `SelectTrigger`; the two are the same manoeuvre.
 *
 * The colour is the only other thing here. No hover override, no transition and
 * no opacity - the ghost variant supplies the hover affordance, and since
 * LF360-1933 this control is always visible rather than revealed on hover.
 *
 * LF360-1933 review M-3: the 16px is the GLYPH's size, not the hit target's.
 * Sizing the box to the glyph is what fixed the alignment, but taken alone it
 * also took the pointer target from 36x36 to 16x16 - a fifth of the area, on a
 * control that deletes a rule. The Figma constrains the drawn icon to 16x16
 * (`x=301` in a 317px frame, 16 wide); it says nothing about how big an area
 * responds to a click, and WCAG 2.2 SC 2.5.8 asks for 24x24 CSS px.
 *
 * `before:-inset-1` buys the 8px back on both axes without moving anything: a
 * pseudo-element belongs to its originating element for hit testing, so the
 * responsive area becomes 24x24 while the box that lays out - and therefore the
 * 7.5px offset the design measures, and the 16x16 the merge has to resolve to -
 * is untouched. Growing the button to `size-6` would have moved the glyph 4px
 * down inside its own box and put the offset back where BRM-02 found it.
 *
 * `relative` is load-bearing, not decoration: without it the pseudo-element
 * anchors to whatever positioned ancestor happens to be nearest, which today is
 * the wrapper div and tomorrow is whatever wraps this control next.
 */
export const rowDeleteButtonClasses = (): string =>
  'relative size-4 p-0 text-label-foreground before:absolute before:-inset-1';

/**
 * LF360-1933: the loading affordance for a single rule row while that rule is
 * pending.
 *
 * This replaced a full-bleed `absolute inset-0 bg-background/10
 * backdrop-blur-[1px] z-50` overlay that covered the entire panel body and a
 * 32px `w-8 h-8 border-4` ring at its centre. The overlay reported on one
 * operation but blocked every rule on screen, which is the defect BRM-03 fixes;
 * the recipe here is deliberately the same ring shrunk to row scale, so the
 * visual language is unchanged while the blast radius is not. Nothing here may
 * position the element out of its row - no `absolute`, no `inset-*`, no
 * stacking index - and styles.test.ts pins that negative half too, because a
 * spinner that escapes its row is the exact thing being removed.
 *
 * `shrink-0` matters because `criteriaRowClasses()` is a wrapping flex row: a
 * 12px square with no flex-basis of its own would otherwise be the first thing
 * the row squeezes when the pills are wide.
 *
 * Both ring colours are theme tokens (`--primary`), not the Figma hex, because
 * the no-raw-colour sweep in styles.test.ts fails the moment one is pasted in.
 *
 * LF360-1933 round 2: the track was written `border-primary/20` and never
 * rendered - see the note above this file's `rowDeleteHighlightClasses` on why
 * a `/N` modifier on a token in this theme silently produces nothing. Until now
 * the ring drew its track in the default border colour and only its top segment
 * in `--primary`, which is not the two-tone ring this comment has claimed since
 * the first round. `color-mix()` makes the 20% real.
 */
export const rowPendingSpinnerClasses = (): string =>
  'size-3 shrink-0 rounded-full border-2 border-[color-mix(in_srgb,var(--primary)_20%,transparent)] border-t-primary animate-spin';

/**
 * LF360-1933 round 2: why no theme colour in this file ever carries a `/N`
 * opacity modifier, however natural `bg-destructive/10` looks.
 *
 * tailwind.config.js maps every token as a bare `var(--x)`, and src/index.css
 * fills those vars with a COMPLETE colour value (`--destructive: hsla(0 84.2%
 * 60.2%)`), not with the bare channels the shadcn form
 * `hsl(var(--x) / <alpha-value>)` expects. Tailwind therefore has nothing to
 * substitute an alpha into, and it does not warn: the utility is dropped, the
 * class name still ships in the markup, and the element renders with no colour
 * at all. Silent, and indistinguishable from a class that simply had no effect.
 *
 * This is not hypothetical. `border-primary/20` shipped in the row spinner
 * below in the first round of LF360-1933 and has never rendered - the ring's
 * track fell back to the default border colour while only `border-t-primary`
 * did what it said. `color-mix()` is the way to blend a token here, and
 * styles.test.ts fails the whole file if a `/N` modifier on a theme token
 * reappears.
 */

/**
 * LF360-1933 round 2: the row highlight that answers "what exactly am I about
 * to delete?" while the pointer sits on a rule's trash.
 *
 * Driven by CSS `:has()`, not by React state. An onMouseEnter/onMouseLeave pair
 * holding a hovered index would re-render the panel twice per pointer pass over
 * a control whose over-rendering is the other half of the same round of
 * feedback - the highlight would be paid for out of the budget it is meant to
 * leave alone. Tailwind 3.4 ships the `has-*` variant, and the delete control's
 * `before:-inset-1` target belongs to the button for hit testing, so the row
 * lights up across the whole 24px target rather than only the 16px glyph.
 *
 * The hook is `data-rule-delete`, a bare attribute, deliberately NOT the
 * control's `data-testid`. That testid is `info=>behavior:rule:remove`, and `=`
 * and `>` inside an arbitrary variant are exactly the characters Tailwind's
 * class parser has to make a judgement call about. A selector that fails to
 * compile emits no CSS and no error, which surfaces as "the highlight just
 * doesn't work" with nothing to grep for.
 *
 * A destructive tint rather than the outline that was also suggested: the row
 * is a run of pills that each already carry their own radius and background, so
 * a 1px outline lands between them and competes with the hairline dividers
 * inside them, while a wash sits behind all of it and reads as one block. The
 * colour says removal specifically, which a neutral grey hover would not.
 *
 * The wash stands 4px clear of the row on every side, which is the gap asked
 * for on 2026-08-28: hugging the pills exactly, it read as a tint applied to
 * the controls rather than as a block containing them.
 *
 * Drawn as a same-colour box-shadow spread rather than as padding, and that is
 * the whole reason this is one line instead of `-m-1 p-1`. Nothing here may
 * change the box. The row is a wrapping flex row whose gaps are pinned to the
 * Figma in criteriaRowClasses(), and it is the positioning context for a delete
 * button placed `absolute right-1 top-[7.5px]` - so growing the row's padding
 * box moves that button 4px out from under the pointer that is hovering it,
 * which is a hover that can drop and retrigger rather than a cosmetic nudge. A
 * spread paints outside the border box and is invisible to layout, and it takes
 * the same `rounded-md` radius, so the result is the wash the design asked for
 * with none of that.
 *
 * The transition names its two properties rather than staying `transition-colors`:
 * box-shadow is not a colour property, and under `transition-colors` the wash
 * would ease while its own 4px border snapped.
 */
export const rowDeleteHighlightClasses = (): string =>
  'rounded-md transition-[background-color,box-shadow] has-[[data-rule-delete]:hover]:bg-[color-mix(in_srgb,var(--destructive)_10%,transparent)] has-[[data-rule-delete]:hover]:shadow-[0_0_0_4px_color-mix(in_srgb,var(--destructive)_10%,transparent)]';

/**
 * Hover/focus-revealed affordances. focus-within is what keeps them
 * reachable by keyboard - a hover-only control is invisible to keyboard and
 * screen-reader users.
 *
 * `scope: 'row'` targets the named `group/row` on a criteria row wrapper
 * instead of the branch-level unnamed `group` - without it, hovering
 * anywhere in the branch would reveal every row's delete button at once,
 * since a row sits nested inside the branch's own `group`. The drag handle
 * (branch-level, not row-scoped) uses the default.
 */
export const revealOnHoverClasses = (scope?: 'row'): string =>
  scope === 'row'
    ? 'opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100'
    : 'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100';

/**
 * LF360-1823: opt-in pill skin for the shared Selectbox / ComboboxBehavior
 * triggers. Returning '' for an absent variant is load-bearing: Selectbox is
 * also used by IfStatementNodeInfo, SliderNodeInfo and GenericNodeInfo, and
 * those must render exactly as before.
 */
export const controlVariantClasses = (variant?: 'pill'): string =>
  variant === 'pill'
    ? 'h-auto w-auto rounded-lg border-0 bg-input pl-2 pr-[7px] py-[7px] text-xs text-foreground'
    : '';

/**
 * LF360-1823 C1: the character cap shared between controlValueVariantClasses'
 * `max-w-[Nch]` below and ComboboxBehavior's clamped `size` attribute on the
 * pill's native `<input>`. One constant, not two hardcoded 30s, so the CSS
 * cap and the intrinsic-size cap that makes it actually engage (see the doc
 * comment below) can't silently drift apart.
 */
export const PILL_VALUE_MAX_CHARS = 30;

/**
 * LF360-1823: neutralises the base chrome (h-9, border, bg-input,
 * shadow-sm, px-3, text-sm) of the value rendered inside a pill-variant
 * ComboboxBehavior trigger. The trigger already supplies the pill's
 * background/border/radius/padding via controlVariantClasses above -
 * without this the value renders its own nested pill inside it (a pill
 * inside a pill).
 *
 * `w-auto` alone is NOT enough to size a native `<input>` to its content -
 * unlike a `<div>`/`<span>`, a text input is a replaced form control whose
 * `width: auto` resolves to the UA-default ~20-character preferred width
 * (driven by the `size` attribute), not to the rendered value's actual
 * length. ComboboxBehavior sets `size` dynamically from the value's length
 * alongside these classes - see the pill branch there.
 *
 * `max-w-[${PILL_VALUE_MAX_CHARS}ch]`, NOT `max-w-full`, caps a long value
 * (a sentence-length scenario question, or a dangling reference's raw
 * 36-char UUID). A percentage max-width resolves against the shrink-to-fit
 * containing block the flex layout computes FROM the input's own intrinsic
 * size - so it does nothing to constrain that intrinsic size in the first
 * place, and a flex item never shrinks below its min-content width
 * regardless of max-width. `size` (a definite character count, clamped in
 * ComboboxBehavior at this same PILL_VALUE_MAX_CHARS) is what makes the
 * min-content width bounded so `truncate` and this max-width can actually
 * engage. Confirmed by measurement: a 99-char label rendered 626px (247px
 * past the 395px panel, NODE_INFO_WIDTH in src/consts.ts) with `max-w-full`
 * alone; capping both `size` and this max-width keeps it inside the panel.
 * (That measurement was taken against the 395px panel this component used to
 * open in; LF360-1933 round 2 widened Behavior Rules to
 * BEHAVIOR_RULES_NODE_INFO_WIDTH. The cap is a character count and does not
 * depend on the panel width, so it survives the change - but the arithmetic
 * quoted above no longer describes today's panel and should not be re-derived
 * from it.)
 */
export const controlValueVariantClasses = (variant?: 'pill'): string =>
  variant === 'pill'
    ? `h-auto w-auto max-w-[${PILL_VALUE_MAX_CHARS}ch] border-0 bg-transparent p-0 shadow-none text-xs truncate`
    : '';

/**
 * LF360-1823: single chevron-down affordance for a pill-variant trigger,
 * matching the design instead of the shadcn default double caret-sort.
 */
export const controlChevronClasses = (): string => 'size-3 shrink-0 opacity-50';

/**
 * LF360-1933 round 2: a pill that states a value instead of offering a choice.
 *
 * Composed from `controlVariantClasses('pill')` rather than restating the skin,
 * so this pill and the Selectbox pill beside it cannot drift apart on the next
 * design pass - they are the same pill, and only one of them owns a chevron.
 * What it leaves out is the trigger: a Select whose menu holds exactly one item
 * costs a click to confirm the only thing it could have said.
 *
 * `opacity-50` when disabled mirrors what every call site already passes to the
 * Selectbox it replaces; without it a disabled rule would draw one pill at full
 * strength beside a faded one.
 *
 * Declared below `controlVariantClasses` on purpose: a `const` arrow function is
 * not hoisted, and reading it from above would be a temporal-dead-zone bug the
 * moment anything called this at module-evaluation time rather than at render.
 */
export const staticPillClasses = ({ disabled }: { disabled?: boolean } = {}): string =>
  cn(controlVariantClasses('pill'), 'whitespace-nowrap', { 'opacity-50': disabled });
